import React, { createContext, useCallback, useContext, useMemo } from 'react';

import { useTranslation } from '@lyearn/i18n';
import { useAccessControl } from '@/components/AccessControl';
import DefaultLoader from '@/components/Loader';
import DeleteCommentDialog from '@/features/messages/components/DeleteCommentDialog';
import { MessageActions } from '@/features/messages/hooks/useMessageAction';
import useMessageMutations from '@/features/messages/hooks/useMessageMutations';
import useMessages from '@/features/messages/hooks/useMessages';
import { ChannelActionsEnum, ChannelSubjectsEnum, PostTypeEnum } from '@/types/schema';

import MessageCounterDivider from './MessageCounterDivider';
import MessageEditorComponent from './MessageEditor';
import MessageItems from './MessageItems';
import { MessageListProps, MessagesContextInterface } from './types';

const MessagesContext = createContext<MessagesContextInterface | null>(null);

export const useMessagesContext = () => {
  const context = useContext(MessagesContext);

  return context ?? ({} as MessagesContextInterface);
};

const DEFAULTS = {
  deleteCommentTitle: 'Common:Message.DeleteComment',
  deleteCommentDescription: 'Common:Message.DeleteCommentDescription',
};

function MessageList(props: MessageListProps) {
  const {
    postId,
    channel,
    children,
    i18n = DEFAULTS,
    isPinned,
    Loader = DefaultLoader,
    adaptMessage,
    pauseSubscription,
    postedByIds,
    snackbarPosition,
    showAllComments,
  } = props;
  const { t } = useTranslation();
  const showHiddenPosts = useAccessControl(
    channel.userPermissions,
    ChannelActionsEnum.Hide,
    postId ? ChannelSubjectsEnum.Reply : ChannelSubjectsEnum.Post,
  );

  const {
    data: messages,
    fetchMore,
    totalCount,
    isFetching,
  } = useMessages({
    channelIds: [channel.id],
    topic: [channel.topic],
    types: postId ? [PostTypeEnum.Reply] : [PostTypeEnum.Post],
    parentId: postId,
    isPinned: isPinned,
    postedByIds,
    adaptMessage: adaptMessage,
    showHiddenPosts,
    pauseSubscription,
    showAllComments,
  });

  const { state, onAction } = useMessageMutations({
    channelId: channel.id,
    snackbarPosition,
  });

  const messageToDelete = state.messageToDeleteId
    ? messages?.find((message) => message.id === state.messageToDeleteId)
    : undefined;

  const onDeleteMessage = useCallback(() => {
    if (state.messageToDeleteId) {
      onAction({
        type: MessageActions.Delete,
        payload: { id: state.messageToDeleteId },
      });
    }
  }, [onAction, state.messageToDeleteId]);

  const onCloseDeleteMessage = useCallback(() => {
    if (state.messageToDeleteId) {
      onAction({
        type: MessageActions.ToggleDelete,
        payload: { id: state.messageToDeleteId },
      });
    }
  }, [onAction, state.messageToDeleteId]);

  const contextValue = useMemo(() => {
    return { messages, postId, channel, onAction, fetchMore, totalCount, state };
  }, [messages, postId, channel, onAction, fetchMore, totalCount, state]);

  if (!messages) {
    return Loader ? <Loader /> : null;
  }

  return (
    <MessagesContext.Provider value={contextValue as MessagesContextInterface}>
      {children}
      {messageToDelete ? (
        <DeleteCommentDialog
          description={t(i18n.deleteCommentDescription)}
          message={messageToDelete}
          title={t(i18n.deleteCommentTitle)}
          onClose={onCloseDeleteMessage}
          onDelete={onDeleteMessage}
        />
      ) : null}
    </MessagesContext.Provider>
  );
}

export {
  MessageCounterDivider as CounterDivider,
  MessageEditorComponent as Editor,
  MessageItems,
  MessageList as Provider,
};
